import React, { useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { detectInputType } from '@/utils/helperFunc';

function Input({
  className,
  onChangeComplete,
  onKeyDown,
  onKeyUp,
  step = 1,
  min,
  max,
  type,
  errorState,
  ...props
}: React.ComponentProps<'input'> & {
  errorState?: boolean;
  onChangeComplete?: (
    event: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>,
  ) => void;
  step?: number;
  min?: number;
  max?: number;
}) {
  const [onChangeCalled, setOnChangeCalled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<{
    isDragging: boolean;
    direction: 'up' | 'down' | null;
    startY: number;
    intervalId: NodeJS.Timeout | null;
    wasValueChanged: boolean; // Track if value was changed during drag
  }>({
    isDragging: false,
    direction: null,
    startY: 0,
    intervalId: null,
    wasValueChanged: false,
  });

  const getInputType = () => {
    return props.value !== undefined ? detectInputType(props.value) : 'text';
  };

  const getCurrentValue = (): number => {
    const value = inputRef.current?.value || '0';
    const numValue = parseFloat(value);
    return isNaN(numValue) ? 0 : numValue;
  };

  const updateValue = (newValue: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (inputRef.current) {
      // Apply min/max constraints
      let constrainedValue = newValue;
      if (min !== undefined && constrainedValue < min) constrainedValue = min;
      if (max !== undefined && constrainedValue > max) constrainedValue = max;

      inputRef.current.value = constrainedValue.toString();

      // Trigger change event
      const event = new Event('input', { bubbles: true });
      inputRef.current.dispatchEvent(event);

      setOnChangeCalled(true);
      dragRef.current.wasValueChanged = true; // Mark that value was changed
      props.onChange?.({
        target: inputRef.current,
        currentTarget: inputRef.current,
        key: e.key,
      } as unknown as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const incrementValue = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = getCurrentValue();
    updateValue(currentValue + step, event);
  };

  const decrementValue = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = getCurrentValue();
    updateValue(currentValue - step, event);
  };

  const onBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onChangeCalled) {
      onChangeComplete?.(event);
      setOnChangeCalled(false);
    }
    props.onBlur?.(event);
  };

  const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (getInputType() === 'number') {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        incrementValue(event);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        decrementValue(event);
      }
    }

    if (event.key === 'Enter') {
      if (onChangeCalled) {
        onChangeComplete?.(event);
        setOnChangeCalled(false);
      }
    }
    onKeyDown?.(event);
  };
  const onKeyUpHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (getInputType() === 'number') {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        if (onChangeCalled) {
          onChangeComplete?.(event);
          setOnChangeCalled(false);
        }
      }
    }

    if (event.key === 'Enter') {
      if (onChangeCalled) {
        onChangeComplete?.(event);
        setOnChangeCalled(false);
      }
    }
    onKeyUp?.(event);
  };

  return (
    <input
      ref={inputRef}
      type={type}
      data-slot="input"
      className={cn(
        'text-button-sm file:text-button-sm flex h-8 w-full min-w-0 rounded-md bg-neutral-50 p-1.5 !text-neutral-900 outline outline-offset-[-1px] outline-transparent transition-all disabled:pointer-events-none disabled:cursor-not-allowed',
        '[appearance:textfield] placeholder-neutral-500 placeholder:text-xs placeholder:font-medium [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        errorState && 'outline-destructive !text-destructive !text-[12px] outline-2',
        className,
      )}
      step={step}
      min={min}
      max={max}
      {...props}
      onChange={(e) => {
        setOnChangeCalled(true);
        props.onChange?.(e);
      }}
      onBlur={onBlurHandler}
      onKeyDown={onKeyDownHandler}
      onKeyUp={onKeyUpHandler}
    />
  );
}

export { Input };
